<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { mediaUrl } from '@core/utils/mediaUrl'
import axios from '@axios'
import { useMessagingStore } from '@/stores/messaging.store'
import { storeToRefs } from 'pinia'
import MessagingThread from '@core/components/MessagingThread.vue'
import RaMessageList from '@core/components/RaMessageList.vue'
import FanProfileDialog from '@core/components/FanProfileDialog.vue'

const messagingStore = useMessagingStore()
const route = useRoute()

const groupedConversations = ref({}) // { restaurantId: { restaurant, conversations[] } }
const loadingInbox = ref(false)
const activeConversationId = ref(null)
const activeConvMessages = ref([])
const loadingMessages = ref(false)
const sendingMessage = ref(false)
const newMessageText = ref('')
const showThread = ref(false)

// The member's profile in a pop-up, from their name and photo (Kapil, 2026-09-25).
const profileOpen = ref(false)

onMounted(async () => {
  await fetchInbox()

  // A notification opens its conversation: /admin/messaging?conv={id}.
  const wanted = route.query.conv
  if (wanted) {
    const conv = Object.values(groupedConversations.value)
      .flatMap(group => group.conversations || [])
      .find(c => String(c._id) === String(wanted))
    if (conv) await selectConversation(conv)
  }
})

async function fetchInbox() {
  loadingInbox.value = true
  try {
    const { data } = await axios.get('/messaging/restaurantAdmin/conversations')
    groupedConversations.value = data.conversations || {}
  } catch (err) {
    console.error('RA inbox fetch error', err)
  } finally {
    loadingInbox.value = false
  }
}

async function selectConversation(conv) {
  activeConversationId.value = conv._id
  showThread.value = true
  loadingMessages.value = true
  try {
    const { data } = await axios.get(`/messaging/restaurantAdmin/conversations/${conv._id}`)
    activeConvMessages.value = data.messages || []

    // Refresh inbox so unread counts / last-message previews reflect the read state.
    messagingStore.fetchRestaurantUnreadCount?.()
  } catch (err) {
    console.error('RA load conversation error', err)
  } finally {
    loadingMessages.value = false
  }
}

async function sendRaMessage() {
  if (!newMessageText.value.trim() || sendingMessage.value) return

  const text = newMessageText.value.trim()
  newMessageText.value = ''

  const conv = findActiveConv()
  if (!conv) return

  sendingMessage.value = true
  try {
    const restaurantId = conv.restaurantId?.toString() || conv.restaurantId
    const { data } = await axios.post(
      `/messaging/restaurant/${restaurantId}/conversations/${activeConversationId.value}/messages`,
      { text }
    )
    if (data.message) {
      activeConvMessages.value.push(data.message)
    }
  } catch (err) {
    console.error('RA send message error', err)
    newMessageText.value = text
  } finally {
    sendingMessage.value = false
  }
}

function findActiveConv() {
  for (const group of Object.values(groupedConversations.value)) {
    const found = group.conversations.find(c => c._id === activeConversationId.value)
    if (found) return found
  }
  
  return null
}

function activeConvTitle() {
  const conv = findActiveConv()
  if (!conv) return ''
  const member = conv.member
  if (member) return `${member.firstName || ''} ${member.lastName || ''}`.trim()
  
  return 'Member'
}

const activeConv = computed(() => (activeConversationId.value ? findActiveConv() : null))

function lastMessagePreview(conv) {
  if (!conv.lastMessage?.text) return 'No messages'
  const t = conv.lastMessage.text
  
  return t.length > 60 ? t.substring(0, 60) + '…' : t
}

function handleKeydown(event) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    sendRaMessage()
  }
}
</script>

<template>
  <div class="ra-messaging d-flex h-100">
    <!-- Left: Grouped inbox -->
    <div
      class="ra-inbox"
      :class="{ 'd-none d-md-flex': showThread }"
    >
      <div class="pa-3 border-b d-flex align-center justify-space-between">
        <span class="text-subtitle-1 font-weight-medium">Messages</span>
        <VBtn
          icon
          size="small"
          variant="text"
          @click="fetchInbox"
        >
          <VIcon size="18">
            mdi-refresh
          </VIcon>
        </VBtn>
      </div>

      <div
        v-if="loadingInbox"
        class="d-flex justify-center py-6"
      >
        <VProgressCircular
          indeterminate
          color="primary"
          size="28"
        />
      </div>

      <div
        v-else-if="Object.keys(groupedConversations).length === 0"
        class="text-center text-medium-emphasis py-6 px-4"
      >
        No messages yet
      </div>

      <div
        v-for="(group, restaurantId) in groupedConversations"
        :key="restaurantId"
        class="ra-group"
      >
        <!-- Restaurant header -->
        <div class="ra-group-header pa-2 px-3 text-caption font-weight-medium text-medium-emphasis">
          {{ group.restaurant?.name || 'Restaurant' }}
        </div>

        <!-- Conversations in this restaurant -->
        <div
          v-for="conv in group.conversations"
          :key="conv._id"
          class="ra-inbox-item d-flex align-start pa-3 cursor-pointer"
          :class="{ 'ra-inbox-item--active': activeConversationId === conv._id }"
          @click="selectConversation(conv)"
        >
          <VAvatar
            size="36"
            class="mr-3"
            color="grey-lighten-3"
          >
            <VImg
              v-if="conv.member?.profileImageId"
              :src="mediaUrl(conv.member.profileImageId, 'thumb')"
            />
            <VIcon
              v-else
              size="16"
            >
              mdi-account
            </VIcon>
          </VAvatar>

          <div class="flex-1 min-width-0">
            <div class="text-body-2 font-weight-medium text-truncate">
              {{ conv.member ? `${conv.member.firstName || ''} ${conv.member.lastName || ''}`.trim() : 'Member' }}
            </div>
            <div class="text-caption text-medium-emphasis text-truncate">
              {{ lastMessagePreview(conv) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right: Thread -->
    <div
      class="ra-thread flex-1"
      :class="{ 'd-none d-md-flex': !showThread }"
    >
      <div
        v-if="!activeConversationId"
        class="d-flex flex-column align-center justify-center h-100 text-medium-emphasis"
      >
        <VIcon
          size="48"
          class="mb-3"
        >
          mdi-message-outline
        </VIcon>
        <span>Select a conversation to reply</span>
      </div>

      <div
        v-else
        class="d-flex flex-column h-100"
      >
        <!-- Thread header -->
        <div class="d-flex align-center pa-3 border-b">
          <VBtn
            icon
            variant="text"
            size="small"
            class="mr-2 d-md-none"
            @click="showThread = false; activeConversationId = null"
          >
            <VIcon>mdi-arrow-left</VIcon>
          </VBtn>
          <!-- Their photo and name open their profile. -->
          <button
            type="button"
            class="ra-thread-who"
            :disabled="!activeConv?.memberId"
            @click="profileOpen = true"
          >
            <VAvatar
              size="32"
              color="grey-lighten-3"
            >
              <VImg
                v-if="activeConv?.member?.profileImageId"
                :src="mediaUrl(activeConv.member.profileImageId, 'thumb')"
              />
              <VIcon
                v-else
                size="16"
              >
                mdi-account
              </VIcon>
            </VAvatar>
            <span class="text-subtitle-2 font-weight-medium">{{ activeConvTitle() }}</span>
          </button>
        </div>

        <!-- Messages -->
        <div class="flex-1 overflow-y-auto pa-3">
          <div
            v-if="loadingMessages"
            class="d-flex justify-center py-4"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="28"
            />
          </div>
          <RaMessageList
            :messages="activeConvMessages"
            :restaurant-name="activeConv?.restaurant?.name || ''"
            :member-is-fan="!!activeConv?.memberIsFan"
          />
        </div>

        <!-- Reply composer -->
        <div class="pa-3 border-t">
          <div class="d-flex align-end gap-2">
            <VTextarea
              v-model="newMessageText"
              placeholder="Reply as restaurant..."
              rows="1"
              auto-grow
              max-rows="4"
              hide-details
              variant="outlined"
              density="compact"
              class="flex-1"
              @keydown="handleKeydown"
            />
            <VBtn
              color="primary"
              icon
              :loading="sendingMessage"
              :disabled="!newMessageText.trim()"
              @click="sendRaMessage"
            >
              <VIcon>mdi-send</VIcon>
            </VBtn>
          </div>
        </div>
      </div>
    </div>
    <FanProfileDialog
      v-model="profileOpen"
      :member-id="activeConv?.memberId ? String(activeConv.memberId) : ''"
    />
  </div>
</template>

<style scoped>
.ra-messaging {
  height: 100%;
}

.ra-thread-who {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
}

.ra-thread-who:disabled {
  cursor: default;
}

.ra-inbox {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--ds-border-default);
  overflow-y: auto;
}

.ra-thread {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
}

.ra-group-header {
  background: var(--ds-surface-alt, #f8f8f9);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ra-inbox-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: background 0.15s;
}

.ra-inbox-item:hover {
  background: var(--ds-surface-alt, #f8f8f9);
}

.ra-inbox-item--active {
  background: var(--ds-brand-primary-light, #e9f4f3);
}

.border-b {
  border-bottom: 1px solid var(--ds-border-default);
}

.border-t {
  border-top: 1px solid var(--ds-border-default);
}

.min-width-0 {
  min-width: 0;
}

@media (max-width: 960px) {
  .ra-inbox {
    width: 100%;
  }
}
</style>
